import { api } from './client';

// Notifications API
export interface Notification {
  id: string;
  type: 'task_assigned' | 'task_due' | 'comment' | 'mention' | 'project_invite' | 'system';
  title: string;
  message?: string;
  link?: string;
  read: boolean; // SQLite stores as INTEGER (0/1), converted to boolean at API boundary
  snoozed_until?: string | null;
  created_at: string;
}

export type NotificationCategory = 'all' | 'action' | 'mentions' | 'updates' | 'snoozed';

// Internal type from backend (before conversion)
interface NotificationFromBackend extends Omit<Notification, 'read'> {
  read: number;
}

// Helper to convert SQLite INTEGER (0/1) to boolean
function convertNotificationRead(notification: NotificationFromBackend): Notification {
  return {
    ...notification,
    read: notification.read === 1,
  };
}

export const notificationsApi = {
  getAll: async (params?: {
    unread?: boolean;
    category?: NotificationCategory;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get<{
      notifications: NotificationFromBackend[];
      unread_count: number;
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    }>('/notifications', {
      unread: params?.unread !== undefined ? String(params.unread) : undefined,
      category: params?.category,
      limit: params?.limit,
      offset: params?.offset,
    });
    return {
      ...response,
      notifications: (response.notifications || []).map(convertNotificationRead),
    };
  },

  getUnreadCount: () => api.get<{ count: number }>('/notifications/count'),

  markAsRead: (id: string) => api.patch<{ message: string }>(`/notifications/${id}/read`),

  markAllAsRead: () => api.patch<{ message: string }>('/notifications/read-all'),

  snooze: async (id: string, snoozedUntil: string | null) => {
    const response = await api.patch<{
      notification: NotificationFromBackend;
      unread_count: number;
    }>(`/notifications/${id}/snooze`, { snoozed_until: snoozedUntil });
    return {
      ...response,
      notification: convertNotificationRead(response.notification),
    };
  },

  delete: (id: string) => api.delete<{ message: string }>(`/notifications/${id}`),
};
